/**
 * Canvas validation utilities for element positioning and configuration.
 */

export interface CanvasElementPosition {
  gridColumn: number;
  gridRow: number;
  columnSpan: number;
  rowSpan: number;
  id?: string;
}

export interface ValidationError {
  type: "overlap" | "configuration" | "sizing";
  elementId?: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Validate that no elements overlap in the grid.
 */
export function validateNoOverlaps(
  elements: CanvasElementPosition[],
  excludeIds?: Set<string>,
): ValidationResult {
  const errors: ValidationError[] = [];
  const occupiedCells = new Map<string, string>(); // "row,col" -> element id

  for (const element of elements) {
    if (excludeIds?.has(element.id || "")) continue;

    for (let r = element.gridRow; r < element.gridRow + element.rowSpan; r++) {
      for (
        let c = element.gridColumn;
        c < element.gridColumn + element.columnSpan;
        c++
      ) {
        const key = `${r},${c}`;
        if (occupiedCells.has(key)) {
          errors.push({
            type: "overlap",
            elementId: element.id,
            message: `Element overlaps with another at row ${r}, column ${c}`,
            details: {
              conflictingElementId: occupiedCells.get(key),
              row: r,
              column: c,
            },
          });
        } else {
          occupiedCells.set(key, element.id || "unknown");
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a batch of new elements against existing elements.
 */
export function validateBatch(
  existingElements: CanvasElementPosition[],
  newElements: CanvasElementPosition[],
): ValidationResult {
  return validateNoOverlaps([...existingElements, ...newElements]);
}
